/**
 * Verify a sale (business lead, 2026-10-27; name / phone / promo code: Rob, the owner and a rep, 2026-10-30).
 *  - Email: the rep types the member's email; the server checks only that it belongs to someone with a NEW membership
 *    (first started within the verify window, default 30 days) and gives the rep the credit (msr_verify_sale).
 *  - Name / Phone / Promo code: the server returns at most 10 masked matches -- first name + last initial, city/state, member
 *    start date, masked email (owner-approved privacy design 2026-10-29) -- each with a single-use token. The rep picks
 *    one and confirms; the server re-checks it and runs the same credit as the email path. Owner 2026-10-30: name,
 *    phone and promo code only inside an activity, and only members whose membership started on the activity's days.
 *    The standalone page (no eventId) verifies by email only.
 * The answer is a status + a message the business can change (msr_portal_settings); never more member details.
 * Standalone (no eventId) or inside an activity (eventId: the sale also counts toward that activity day's close rate).
 */
import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  confirmSaleMatch,
  msrErrorText,
  nameLooksSearchable,
  phoneDigits,
  searchSaleMatches,
  verifySale,
  type ConfirmResult,
  type SaleMatch,
  type SaleSearchKind,
  type SaleSearchResult,
  type VerifyResult,
} from "../../services/msrFeedback.service";
import { dayRunKeys } from "../../services/msrDayRun.service";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Mode = "email" | SaleSearchKind;
type AnyStatus = VerifyResult["status"] | ConfirmResult["status"];

const TONE: Record<AnyStatus, string> = {
  verified: "border-green-300 bg-green-50 text-green-900",
  already_yours: "border-green-300 bg-green-50 text-green-900",
  already_other: "border-amber-300 bg-amber-50 text-amber-900",
  not_verified: "border-red-300 bg-red-50 text-red-900",
  not_new_sale: "border-amber-300 bg-amber-50 text-amber-900",
  rate_limited: "border-amber-300 bg-amber-50 text-amber-900",
  token_invalid: "border-amber-300 bg-amber-50 text-amber-900",
  token_expired: "border-amber-300 bg-amber-50 text-amber-900",
};

const MODE_LABEL: Record<Mode, string> = {
  name: "Name",
  email: "Email",
  phone: "Phone",
  promo: "Promo code",
};

const FIELD: Record<
  Mode,
  {
    label: string;
    type: string;
    inputMode: "email" | "tel" | "text";
    placeholder: string;
    hint: string | null;
  }
> = {
  name: {
    label: "Member's name",
    type: "text",
    inputMode: "text",
    placeholder: "Last name, or first and last",
    hint: "Type the member's full last name (first name optional). Members who joined on this activity's days appear as you type (up to 10).",
  },
  email: {
    label: "Member's email",
    type: "email",
    inputMode: "email",
    placeholder: "name@example.com",
    hint: null,
  },
  phone: {
    label: "Member's phone number",
    type: "tel",
    inputMode: "tel",
    placeholder: "(512) 555-0100",
    hint: "Shows members who joined on this activity's days (up to 10). Pick the member you signed up.",
  },
  promo: {
    label: "Promo code",
    type: "text",
    inputMode: "text",
    placeholder: "Code the member used",
    hint: "Shows new memberships that used this code on this activity's days (up to 10).",
  },
};

function looksValid(mode: Mode, v: string): boolean {
  if (mode === "email") {
    return EMAIL_RE.test(v);
  }
  if (mode === "phone") {
    return phoneDigits(v) !== null;
  }
  if (mode === "name") {
    return nameLooksSearchable(v);
  }
  return v.length >= 2 && v.length <= 64;
}

function submitLabel(mode: Mode, emailBusy: boolean, searchBusy: boolean) {
  if (mode === "email") {
    return emailBusy ? "Checking…" : "Verify";
  }
  return searchBusy ? "Searching…" : "Search";
}

const startLabel = (iso: string | null) =>
  iso
    ? new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        timeZone: "UTC",
      })
    : "";

function matchLabel(mt: SaleMatch): string {
  const name = [mt.first_name, mt.last_initial ? `${mt.last_initial}.` : null]
    .filter(Boolean)
    .join(" ");
  return name || "Member";
}

const VerifySaleForm: React.FC<{ eventId?: string; dayLabel?: string }> = ({
  eventId,
  dayLabel,
}) => {
  const queryClient = useQueryClient();
  const idSuffix = eventId ?? "any";
  const modes: Mode[] = eventId
    ? ["name", "email", "phone", "promo"]
    : ["email"];
  const [mode, setMode] = useState<Mode>("email");
  const [value, setValue] = useState("");
  const [result, setResult] = useState<
    | {
        kind: "result";
        status: AnyStatus;
        message: string;
        attached?: boolean;
        label: string;
      }
    | { kind: "error"; text: string }
    | null
  >(null);
  const [search, setSearch] = useState<SaleSearchResult | null>(null);
  const [picked, setPicked] = useState<string | null>(null);

  const trimmed = value.trim();
  const valid = looksValid(mode, trimmed);
  const field = FIELD[mode];

  const credited = () => {
    queryClient.invalidateQueries({ queryKey: ["msr-my-verified-sales"] });
    queryClient.invalidateQueries({ queryKey: ["msr-sale-totals"] });
    if (eventId) {
      // the activity's close-rate numbers now include this sale
      queryClient.invalidateQueries({ queryKey: dayRunKeys.state(eventId) });
      queryClient.invalidateQueries({
        queryKey: ["event-attribution-summary", eventId],
      });
      queryClient.invalidateQueries({
        queryKey: ["event-close-rate-stats", eventId],
      });
    }
  };

  // External audit 7af59b8e P2-1: ONE request sequence for every search (live name, phone / promo, Search again).
  // Any mode change, text change or newer request bumps it; a response is applied only if it is still the latest
  // AND for the mode on screen, so a slow name response can never overwrite newer phone results (and their tokens).
  const reqSeq = useRef(0);
  const modeRef = useRef(mode);
  modeRef.current = mode;

  // the live name request whose spinner is showing; its own finally clears it even when it is no longer current
  // (external audit 5967054d minor: leaving Name mode mid-request left liveBusy stuck, so "Search again" stayed disabled)
  const liveReq = useRef(0);
  const [liveBusy, setLiveBusy] = useState(false);

  const reset = (next?: Mode) => {
    reqSeq.current += 1;
    setLiveBusy(false);
    if (next) {
      setMode(next);
    }
    setValue("");
    setResult(null);
    setSearch(null);
    setPicked(null);
  };

  const emailMut = useMutation({
    mutationFn: (e: string) => verifySale(e, eventId ?? null),
    onSuccess: (r, e) => {
      setResult({
        kind: "result",
        status: r.status,
        message: r.message,
        attached: r.attached_to_activity,
        label: e,
      });
      if (r.status === "verified" || r.status === "already_yours") {
        setValue("");
      }
      credited();
    },
    onError: (err) => setResult({ kind: "error", text: msrErrorText(err) }),
  });

  const searchMut = useMutation({
    onMutate: () => ({ seq: ++reqSeq.current }),
    mutationFn: (v: { kind: SaleSearchKind; q: string }) =>
      searchSaleMatches(v.kind, v.q, eventId ?? null),
    onSuccess: (r, v, ctx) => {
      if (ctx?.seq !== reqSeq.current || v.kind !== modeRef.current) {
        return; // stale: a newer search, or another mode, is on screen
      }
      setSearch(r);
      setPicked(null);
      setResult(null);
    },
    onError: (err, v, ctx) => {
      if (ctx?.seq !== reqSeq.current || v.kind !== modeRef.current) {
        return;
      }
      setSearch(null);
      setResult({ kind: "error", text: msrErrorText(err) });
    },
  });

  const confirmMut = useMutation({
    mutationFn: (token: string) => confirmSaleMatch(token),
    onSuccess: (r, token) => {
      const mt = search?.matches.find((x) => x.token === token);
      setResult({
        kind: "result",
        status: r.status,
        message: r.message,
        attached: r.attached_to_activity,
        label: mt ? matchLabel(mt) : "",
      });
      // Every answer ends this list (owner 2026-10-30: after "already verified by another rep" too, so the next row
      // can't just be picked; the server consumed the whole list). A new search starts over.
      setSearch(null);
      setPicked(null);
      if (r.status === "verified" || r.status === "already_yours") {
        setValue("");
        credited();
      }
    },
    onError: (err) => setResult({ kind: "error", text: msrErrorText(err) }),
  });

  // Name: search as the rep types (owner 2026-10-30), debounced ~300 ms. The server lists members only once a FULL last
  // name matches, and never suggests names. Only the newest request may write results.
  // "Search again" re-runs the same name without editing it (delta audit P3); it counts like any search.
  const [rerun, setRerun] = useState(0);
  useEffect(() => {
    if (mode !== "name") {
      return;
    }
    const q = value.trim();
    const gen = ++reqSeq.current;
    const isCurrent = () =>
      gen === reqSeq.current && modeRef.current === "name";
    if (!nameLooksSearchable(q)) {
      setLiveBusy(false);
      return;
    }
    const timer = setTimeout(async () => {
      liveReq.current = gen;
      setLiveBusy(true);
      try {
        const r = await searchSaleMatches("name", q, eventId ?? null);
        if (isCurrent()) {
          setSearch(r);
          setPicked(null);
        }
      } catch (err) {
        if (isCurrent()) {
          setSearch(null);
          setResult({ kind: "error", text: msrErrorText(err) });
        }
      } finally {
        if (liveReq.current === gen) {
          setLiveBusy(false);
        }
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [mode, value, eventId, rerun]);

  const waitingForName = mode === "name" && search?.status === "no_match";
  const busy =
    emailMut.isPending || searchMut.isPending || confirmMut.isPending;
  const inputId = `verify-${mode}-${idSuffix}`;

  return (
    <div className="space-y-3" data-testid="msr-verify-sale-form">
      {eventId ? (
        <div>
          <div
            className="mb-1 block text-sm font-medium text-gray-800"
            id={`verify-mode-${idSuffix}`}
          >
            Search by
          </div>
          <div
            className="inline-flex rounded-md border border-gray-300 bg-white p-0.5"
            role="group"
            aria-labelledby={`verify-mode-${idSuffix}`}
            data-testid="msr-verify-mode"
          >
            {modes.map((md) => (
              <button
                key={md}
                type="button"
                aria-pressed={mode === md}
                onClick={() => mode !== md && reset(md)}
                className={`min-h-[40px] rounded px-3 text-sm font-medium ${
                  mode === md
                    ? "bg-blue-600 text-white"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {MODE_LABEL[md]}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <p
          className="text-xs text-gray-500"
          data-testid="msr-verify-activity-hint"
        >
          To find the member by name, phone or promo code, open the activity you
          sold at and verify the sale there (it lists members who joined on that
          activity's days).
        </p>
      )}

      <form
        className="space-y-2"
        onSubmit={(ev) => {
          ev.preventDefault();
          if (!valid || busy) {
            return;
          }
          if (mode === "email") {
            emailMut.mutate(trimmed);
          } else if (mode !== "name") {
            searchMut.mutate({ kind: mode, q: trimmed });
          }
        }}
      >
        <label
          className="block text-sm font-medium text-gray-800"
          htmlFor={inputId}
        >
          {field.label}
        </label>
        <div className="flex gap-2">
          <input
            id={inputId}
            type={field.type}
            inputMode={field.inputMode}
            autoComplete="off"
            autoCapitalize={
              {
                name: "words",
                email: "none",
                phone: "none",
                promo: "characters",
              }[mode]
            }
            value={value}
            onChange={(e) => {
              reqSeq.current += 1; // an in-flight response is for the old text
              setValue(e.target.value);
              setResult(null);
              setSearch(null);
              setPicked(null);
            }}
            placeholder={field.placeholder}
            className="min-h-[44px] min-w-0 flex-1 rounded-md border border-gray-300 px-3 text-base"
          />
          {mode !== "name" && (
            <button
              type="submit"
              disabled={!valid || busy}
              className="min-h-[44px] rounded-md bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {submitLabel(mode, emailMut.isPending, searchMut.isPending)}
            </button>
          )}
        </div>
        {mode === "name" && liveBusy && (
          <p
            className="text-xs text-gray-500"
            data-testid="msr-verify-name-busy"
          >
            Searching…
          </p>
        )}
        {field.hint && <p className="text-xs text-gray-500">{field.hint}</p>}
        {eventId && dayLabel && (
          <p className="text-xs text-gray-500">
            Counts toward this activity on {dayLabel}.
          </p>
        )}
      </form>

      {waitingForName && (
        <p
          className="text-sm text-gray-600"
          role="status"
          data-testid="msr-verify-name-waiting"
        >
          No member yet. Keep typing the full last name, or check the spelling.
        </p>
      )}
      {search && mode !== "email" && !waitingForName && (
        <div className="space-y-2" data-testid="msr-verify-matches">
          {search.status !== "matches" ? (
            <div
              className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              role="status"
              data-testid="msr-verify-search-empty"
              data-status={search.status}
            >
              {search.message ??
                "No new membership matches that. Check it, or try the member's email."}
            </div>
          ) : (
            <>
              {search.truncated && (
                <div
                  className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
                  role="status"
                  data-testid="msr-verify-search-refine"
                >
                  {search.message ??
                    "More than 10 new memberships match. Showing the first 10 — add more detail or search by email."}
                </div>
              )}
              <p className="text-sm text-gray-700">
                {search.matches.length === 1
                  ? "1 new membership matches. Is this the member you signed up?"
                  : `${search.matches.length} new memberships match. Pick the member you signed up.`}
              </p>
              <ul className="space-y-1.5" aria-label="Matching members">
                {search.matches.map((mt) => {
                  const on = picked === mt.token;
                  const place = [mt.city, mt.state].filter(Boolean).join(", ");
                  return (
                    <li key={mt.token}>
                      <button
                        type="button"
                        aria-pressed={on}
                        onClick={() => {
                          setPicked(on ? null : mt.token);
                          setResult(null);
                        }}
                        data-testid="msr-verify-match"
                        className={`w-full rounded-md border p-3 text-left text-sm ${
                          on
                            ? "border-blue-600 bg-blue-50 ring-1 ring-blue-600"
                            : "border-gray-200 bg-white hover:bg-gray-50"
                        }`}
                      >
                        <div className="font-medium text-gray-900">
                          {matchLabel(mt)}
                          {place ? (
                            <span className="font-normal text-gray-600">
                              {" "}
                              · {place}
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs text-gray-600">
                          {mt.member_start
                            ? `Member since ${startLabel(mt.member_start)}`
                            : "New member"}
                          {" · "}
                          <span className="break-all">
                            {mt.masked_email ?? "no email on file"}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
              <button
                type="button"
                disabled={!picked || busy}
                onClick={() => picked && confirmMut.mutate(picked)}
                className="min-h-[44px] w-full rounded-md bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                {confirmMut.isPending ? "Verifying…" : "Verify this sale"}
              </button>
            </>
          )}
        </div>
      )}

      {!search && result && mode !== "email" && valid && (
        <button
          type="button"
          disabled={busy || liveBusy}
          onClick={() => {
            setResult(null);
            if (mode === "name") {
              setRerun((n) => n + 1);
            } else {
              searchMut.mutate({ kind: mode, q: trimmed });
            }
          }}
          className="min-h-[40px] rounded-md border border-blue-600 px-3 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
          data-testid="msr-verify-search-again"
        >
          Search again
        </button>
      )}
      {result?.kind === "result" && (
        <div
          className={`rounded-md border p-3 text-sm ${TONE[result.status]}`}
          role="status"
          data-testid="msr-verify-result"
          data-status={result.status}
        >
          {result.label && (
            <div className="font-medium break-all">{result.label}</div>
          )}
          <div>{result.message}</div>
          {result.attached && (
            <div className="mt-1 text-xs">
              It now counts toward this activity too.
            </div>
          )}
        </div>
      )}
      {result?.kind === "error" && (
        <div
          className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900"
          role="alert"
        >
          {result.text}
        </div>
      )}
    </div>
  );
};

export default VerifySaleForm;
